import QuestionNumber from "./QuestionNumber";
import {relativeTime, RelativeTime} from "../utils";
import {Board, Question, QuestionStatus} from "../javaTypes";
import {Button} from "react-bootstrap";
import './QuestionAnswer.css'
import React, {MouseEvent, ReactNode, useContext, useId, useState} from "react";
import {ReferenceContext, useOptionalApplyReferences} from "./ReferenceProvider";
import {useQuery} from "@tanstack/react-query";
import {t} from "ttag";
import { useInView } from "react-intersection-observer";
import {Tooltip} from "react-tooltip";

type NestingState = {
    nested: boolean,
    hideMeta: boolean, // we only want to show meta in nested questions on the answer edit view
    canEdit: boolean,
    layer: number,
    hideChildCallback?: () => void
}

export function useQuestionQuery(board: Board, questionId: string | number | undefined, loadedQuestions?: Question[],) {
    return useQuery<Question | undefined>({
        queryKey: ["qa", questionId],
        queryFn: (ctx) =>
            fetch(`/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}/${typeof questionId === 'string' ? "qid" : "qnum"}/${ctx.queryKey[1]}`, {})
                .then(r => r.json()).then(d => new Promise(res => {
                return res(Object.assign({}, d, {
                    createdAt: new Date(d.createdAt).getTime(),
                    modifiedAt: new Date(d.modifiedAt).getTime(),
                    submittedAt: new Date(d.submittedAt).getTime(),
                    answer: d.answer ? Object.assign({}, d.answer, {
                        createdAt: new Date(d.answer.createdAt).getTime(),
                        modifiedAt: new Date(d.answer.modifiedAt).getTime(),
                    }) : undefined
                }));
            })),
        enabled: !!questionId && !loadedQuestions?.some(x => x.questionId === questionId),
        staleTime: Infinity,
        retry: false
    });
}

function QuestionAsker({question}: { question: Question }) {
    const tooltipID = useId();
    let askerName = `${question.askerAccount.firstName ?? ""} ${question.askerAccount.lastName ?? ""}`
    if(question.askerTeam) {
        return <span><a data-tooltip-id={tooltipID} data-tooltip-content={`Team ${question.askerTeam.number} - ${question.askerTeam.shortName}, ${question.askerTeam.city}, ${question.askerTeam.stateProv}, ${question.askerTeam.country}`}>
                <abbr>Team {question.askerTeam.number}</abbr>
            </a> ({askerName})
            <Tooltip id={tooltipID}></Tooltip>
        </span>
    } else {
        return askerName
    }
}

export default function QuestionAnswer({board, question, linkHeader, canEditAnswer, canEditQuestion, editing, hideMeta, canFollowUp, isFollowing, toggleFollow, nesting, loadedQuestions = [], onEditQuestion, saveQuestionAsComment, onEditAnswer, onEditFollowup, onFlag} : {
    board: Board,
    question: Question,
    linkHeader?: boolean,
    canEditAnswer: boolean,
    canEditQuestion: boolean,
    editing: boolean,
    hideMeta?: boolean,
    canFollowUp?: boolean,
    isFollowing?: boolean,
    toggleFollow?: () => void,
    // If the "only nest for first, further nesting becomes prepend" is _not_ desired, always keep this variable false
    nesting?: NestingState,
    loadedQuestions?: Question[],
    onEditQuestion?: () => void,
    saveQuestionAsComment?: () => void,
    onEditAnswer?: () => void,
    onEditFollowup?: () => void,
    onFlag?: () => void
}) {
    const [showBase, setShowBase] = useState(false);
    const { ref, inView } = useInView({
        /* Optional options */
        threshold: 0,
    });
    const {data: fetchBase} = useQuestionQuery(board, question.followupToId, loadedQuestions);
    const references = useContext(ReferenceContext);
    // if starts with digit is a CM/GM section
    const isSectionString = (s?: string) => !!s && s.length && s.charAt(0) >= '0' && s.charAt(0) <= '9';
    const applyTopicName = (s?: string) => !!s && s.length ? (references?.customTopics[s]?.name ?? "") : "";
    const questionContent = useOptionalApplyReferences(inView, question.content, board, references);
    const answerContent = useOptionalApplyReferences(inView, question.answer?.content, board, references);
    const ruleContent = useOptionalApplyReferences(inView, question.rule, board, references);
    const sectionContent = useOptionalApplyReferences(inView, "Section " + question.topic, board, references);
    const questionUrl = `/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}/qa/${question.number}`;
    const base = (!question.followupToId ? undefined : loadedQuestions.find(x => x.questionId === question.followupToId)) ?? fetchBase;

    const collapseSelf = (e: MouseEvent) => {
        if (nesting && nesting.hideChildCallback) {
            nesting.hideChildCallback();
        }
    }

    let followupNesting = !question.followupToId || !base ? null : <div>
        {/* Top-level followup info for the "current" question - "current" question is the one being viewed - the bottom/leaf of the chain/tree */}
        {!nesting?.nested ? <>
            <em>{t`Follow-up to `}</em>
            <QuestionNumber board={board} question={base}/>&nbsp;
            <Button className={"followup"} size={"sm"} variant={"info"} onClick={e => setShowBase(!showBase)}>{showBase ? "Hide" : "Show Inline"}{showBase ? <>&#9650;</> : <>&#9660;</>}</Button>&nbsp;
            {canEditQuestion && !!nesting && onEditFollowup ? <Button className={"followup"} size={"sm"} variant={"secondary"} onClick={onEditFollowup}>Edit Link&nbsp;&nbsp;<i className={"fa fa-pencil"}/> </Button> : null}
        </> : null}

        {/* Nesting info for the highest expanded element in the chain/tree (provided it is NOT the top)*/}
        {nesting?.nested && !showBase ? <><em>{t`Multi-question chain with `}</em><QuestionNumber board={board} question={base}/>
            &nbsp;<Button className={"followup"} size={"sm"} variant={"info"} onClick={e => setShowBase(!showBase)}>{t`Prepend One`}&#9650;</Button>
            {nesting!.layer > 1 ? <>&nbsp;<Button className={"followup"} size={"sm"} variant={"info"} onClick={collapseSelf}>{t`Collapse One`}&#9660;</Button></> : null}
        </> : null}

        <div style={{marginLeft: nesting?.nested ? 'initial' : '2rem', marginTop: '.5rem'}} hidden={!showBase}>
            <QuestionAnswer board={board} question={base} linkHeader={linkHeader} canEditQuestion={false} canEditAnswer={false} editing={false} hideMeta={nesting?.hideMeta ?? true} loadedQuestions={loadedQuestions}
                            nesting={{nested: true, layer: (nesting?.layer ?? 0) + 1, hideMeta: (nesting?.hideMeta ?? true), canEdit: !!nesting?.canEdit, hideChildCallback: () => setShowBase(false)}}/>
        </div>
    </div>

    // Handle the root question / top of the chain, but don't show a single follow-up as a chain (>1)
    if ((nesting?.layer ?? 0) > 1 && !followupNesting) {
        followupNesting = <><em>{t`Multi-question chain`}</em> <Button className={"followup"} size={"sm"} variant={"info"} onClick={collapseSelf}>{t`Collapse One`}&#9660;</Button></>
    }

    let followIcon: ReactNode = undefined
    if(isFollowing !== undefined) {
        followIcon = <a className={"icon-link text-black text-decoration-none"} onClick={toggleFollow} title={isFollowing ? t`Unfollow` : t`Follow`}>
            <i className={`${isFollowing ? 'fas text-warning' : 'far'} fa-star`} />
        </a>
    }
    const headerContent = <><QuestionNumber board={board} question={question}/> {question.title}</>

    return <div className={"qa"} ref={ref}>
        <div className={"question-wrapper"}>
            {/*Show all but the first question in the follow-up chain before the next one in the chain*/}
            {nesting?.nested ? followupNesting : null}
            <div className={"d-flex justify-content-between"}>
                {linkHeader ? <h5>{followIcon} <a href={questionUrl}>{headerContent}</a></h5> : <h5>{followIcon} {headerContent}</h5>}
                <div className={"d-flex justify-content-end"}>
                    <Button variant={"info"} size={"sm"} className={"p-1 m-1"} hidden={!canEditQuestion || !!question.followupToId || !onEditFollowup || !board.config.enableFollowups} onClick={onEditFollowup}>Set as Follow-up</Button>
                    <Button variant={"secondary"} size={"sm"} className={"p-1 m-1"} hidden={!canEditQuestion} onClick={saveQuestionAsComment}>Save to Comment</Button>
                    <Button variant={"secondary"} size={"sm"} className={"p-1 m-1"} hidden={!canEditQuestion} onClick={onEditQuestion}>Edit Question</Button>
                </div>
            </div>
            {/* Show the first layer of the follow-up chain under the title of the current question*/}
            {!nesting?.nested ? followupNesting : null}
            <div dangerouslySetInnerHTML={{__html: questionContent}} style={{lineHeight: '1.3'}}></div>
            <div className={"d-flex justify-content-end"} hidden={hideMeta}>
                <small style={{marginBottom: canEditQuestion ? '-1rem' : undefined, marginRight: '1ch'}} hidden={!canEditQuestion || !!question.flaggedBy}><em>
                    <i className={"fa fa-flag flagForDiscussion"} title={"Flag for discussion"} onClick={onFlag}/>
                </em></small>
                <small className={"metaInfo"} style={{marginBottom: canEditQuestion ? '-1rem' : undefined}}>
                    Asked <RelativeTime date={question.submittedAt}/>{canEditQuestion || nesting?.canEdit ? <> by <QuestionAsker question={question} /></> : null}
                </small>
            </div>
            <hr hidden={!canEditQuestion}/>
        </div>
        {!!question?.answer && (question?.status === QuestionStatus.ANSWERED || canEditAnswer) ? <div>
            <div className={"d-flex justify-content-between"}>
                <h5>{(canEditAnswer || canEditQuestion) && question.status === QuestionStatus.ANSWERED ? "Published" : null} Answer</h5>
                <Button variant={"primary"} size={"sm"} className={"p-1 m-1"} hidden={!canEditAnswer || editing} disabled={!!question.flaggedBy} onClick={onEditAnswer}>Edit Answer</Button>
            </div>
            <div dangerouslySetInnerHTML={{__html: answerContent}} style={{lineHeight: '1.3'}}></div>
            <small className={"d-flex justify-content-between"} hidden={hideMeta}>
                <div className={"d-flex gap-4"} style={{marginTop: 'auto'}}>
                    <div hidden={(question.rule?.length ?? 0) < 1} dangerouslySetInnerHTML={{__html: ruleContent}}/>
                    <div hidden={(question.topic?.length ?? 0) < 1} dangerouslySetInnerHTML={{__html: isSectionString(question.topic) ? sectionContent : `<em>Topic: ${applyTopicName(question.topic)}</em>`}}/>
                </div>
                <div className={"d-flex justify-content-end"}>
                    <div style={{marginTop: 'auto', marginRight: '1ch'}} hidden={!canFollowUp || !board.config.enableFollowups}><em>
                        <a className={"askFollowup"} href={`${board.slug}/ask/new?followup=${question.number}`}>Ask a follow-up</a>
                    </em></div>
                    <div className={"metaInfo"}>
                        Published <RelativeTime date={question.answer.createdAt}/>
                        {question.answer.modifiedAt && question.answer.modifiedAt !== question.answer.createdAt ? <><br/>Updated <RelativeTime date={question.answer.modifiedAt}/></> : null}
                    </div>
                </div>
            </small>
            <hr style={{marginBlockStart: 0, marginTop: 0}}/>
        </div> : null}
    </div>
}